import React from 'react';
import { AdminStats as AdminStatsType } from '@curbside-quest/shared';

interface AdminStatsProps {
  stats: AdminStatsType;
  detailed?: boolean;
}

export const AdminStats: React.FC<AdminStatsProps> = ({ stats, detailed = false }) => {
  const totalReports = Object.values(stats.reportCounts).reduce((sum, count) => sum + count, 0);

  const statCards = [
    {
      title: 'Pending Reports',
      value: stats.reportCounts.pending,
      icon: '⏳',
      color: 'yellow',
      description: 'Reports awaiting review'
    },
    {
      title: 'Active Reports',
      value: stats.reportCounts.active,
      icon: '✅',
      color: 'green',
      description: 'Approved and live reports'
    },
    {
      title: 'Going Soon',
      value: stats.reportCounts.going_soon,
      icon: '🚚',
      color: 'orange',
      description: 'Scheduled for pickup soon'
    },
    {
      title: 'Total Users',
      value: stats.userCount,
      icon: '👥',
      color: 'blue',
      description: 'Registered users'
    },
    {
      title: 'Reports This Week',
      value: stats.recentActivity.reportsLastWeek,
      icon: '📈',
      color: 'purple',
      description: 'New reports in last 7 days'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      yellow: 'bg-treasure-50 text-treasure-800 border-treasure-200',
      green: 'bg-verge-50 text-verge-800 border-verge-200',
      blue: 'bg-cardboard-50 text-cardboard-800 border-cardboard-200',
      purple: 'bg-urban-50 text-urban-800 border-urban-200',
      red: 'bg-rust-50 text-rust-800 border-rust-200',
      orange: 'bg-cardboard-100 text-cardboard-800 border-cardboard-200'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`border rounded-lg p-6 ${getColorClasses(stat.color)}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-75">{stat.title}</p>
                <p className="text-3xl font-bold mt-1">{stat.value.toLocaleString()}</p>
                {detailed && (
                  <p className="text-xs opacity-60 mt-1">{stat.description}</p>
                )}
              </div>
              <div className="text-2xl opacity-75">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Stats */}
      {detailed && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Report Status Breakdown */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Status Breakdown</h3>
            <div className="space-y-4">
              {Object.entries(stats.reportCounts).map(([status, count]) => {
                const percentage = totalReports > 0 ? (count / totalReports) * 100 : 0;
                const statusConfig = {
                  pending: { label: 'Pending Review', color: 'bg-yellow-500', icon: '⏳' },
                  active: { label: 'Active', color: 'bg-green-500', icon: '✅' },
                  going_soon: { label: 'Going Soon', color: 'bg-orange-500', icon: '🚚' },
                  rejected: { label: 'Rejected', color: 'bg-red-500', icon: '❌' },
                  gone: { label: 'Gone', color: 'bg-gray-500', icon: '🗑️' }
                } as const;
                const config = statusConfig[status as keyof typeof statusConfig];

                if (!config) {
                  return null;
                }

                return (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{config.icon}</span>
                      <span className="font-medium text-gray-900">{config.label}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${config.color}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600 w-12 text-right">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">Total Reports</span>
                <span className="font-bold text-lg text-gray-900">{totalReports}</span>
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">📈</span>
                  <div>
                    <p className="font-medium text-gray-900">Reports This Week</p>
                    <p className="text-sm text-gray-600">New submissions</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-purple-600">
                  {stats.recentActivity.reportsLastWeek}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">👥</span>
                  <div>
                    <p className="font-medium text-gray-900">Total Users</p>
                    <p className="text-sm text-gray-600">Registered accounts</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {stats.userCount}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">⚡</span>
                  <div>
                    <p className="font-medium text-gray-900">Approval Rate</p>
                    <p className="text-sm text-gray-600">Active vs rejected</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-yellow-600">
                  {stats.reportCounts.active + stats.reportCounts.rejected > 0
                    ? Math.round((stats.reportCounts.active / (stats.reportCounts.active + stats.reportCounts.rejected)) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {!detailed && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="flex items-center justify-center space-x-2 p-3 border border-yellow-300 rounded-lg text-yellow-700 hover:bg-yellow-50 transition-colors">
              <span>⏳</span>
              <span>Review Pending ({stats.reportCounts.pending})</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-3 border border-orange-300 rounded-lg text-cardboard-700 hover:bg-cardboard-50 transition-colors">
              <span>🚚</span>
              <span>Monitor Going Soon ({stats.reportCounts.going_soon})</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-3 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors">
              <span>👥</span>
              <span>Manage Users</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
              <span>📊</span>
              <span>View Audit Log</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};